// Dynamic Make -> Model -> Gen/Year pills, shared by collection pages AND search-results
// pages (mode picked up from the #es-vehicle-pills element's data-mode attribute). Pulls the
// SAME /fitment-live data the homepage selector uses, and the SAME shared tag-alias maps
// (assets/vehicle-tag-maps.js, loaded before this file) -- so all three surfaces resolve a
// given make/model to the exact same tag, eliminating the drift that came from each surface
// keeping its own independent copy of these maps.
(function(){
  function esR8Labels(data) {
    if (!data || !data.Audi || typeof data.Audi !== 'object') return data;
    Object.keys(data.Audi).filter(function(model) { return /^R8(?:\b|$)/i.test(model); }).forEach(function(model) {
      var entries = data.Audi[model];
      if (!Array.isArray(entries)) return;
      entries.forEach(function(entry) {
        if (!entry || typeof entry.label !== 'string') return;
        entry.label = entry.label.replace(/\b(?:Type\s+)?42\b/gi, 'Gen 1').replace(/\b(?:Type\s+)?4S\b/gi, 'Gen 2');
      });
    });
    return data;
  }

  const root = document.getElementById('es-vehicle-pills');
  if (!root) return;

  const VT = window.ESVehicleTags;
  if (!VT) return; // vehicle-tag-maps.js didn't load -- fail closed, no broken pills

  const { MAKE_TAGS, MODEL_TAGS, aliasesFor, primaryTag } = VT;

  const FITMENT_URL = root.getAttribute('data-fitment-url') || '';
  const mode = root.getAttribute('data-mode') || 'collection';
  const brandHex = root.getAttribute('data-brand-hex') || '#12A8F6';

  // --- Collection mode setup ---
  const collectionUrl = (root.getAttribute('data-collection-url') || '').replace(/\/$/, '');
  const forcedMakeTag = (root.getAttribute('data-forced-make') || '').trim().toLowerCase();
  const currentTagsAttr = (root.getAttribute('data-current-tags') || '').split('|').map(t => t.trim().toLowerCase()).filter(Boolean);

  // Currently-checked Category/Brand filter checkboxes (assets/category-brand-filters.js) --
  // included in every pill's existence check so a Make pill for e.g. "Infiniti" doesn't show
  // up while a Brand filter for a make that never sold anything Infiniti-compatible (like APR)
  // is active. Read live each time render() runs, not cached, since the user can toggle these
  // after pills already rendered once.
  function activeFilterTags(){
    return Array.from(document.querySelectorAll('input[name="es-category"]:checked, input[name="es-brand"]:checked'))
      .map(i => i.value);
  }

  // --- Search mode setup ---
  const rawSearchTerms = root.getAttribute('data-search-terms') || '';
  // Quote any tag containing a space OR a period -- unquoted `tag:mk7.5` gets tokenized by
  // Shopify's search index (confirmed live: it matched plain "mk7"-tagged products too, a
  // false positive), while quoting forces an exact phrase match.
  function tagTerm(t){ return /[\s.]/.test(t) ? `tag:"${t}"` : `tag:${t}`; }
  // A generation tag qualified "facelift"/"pre-facelift"/"lci" (e.g. "8v facelift") only
  // matches products genuinely facelift-specific -- most engine/drivetrain parts correctly
  // carry just the bare parent code ("8v") since they fit the whole generation. Match the
  // qualified tag OR its bare parent, same fix applied to assets/ymm-filter.js and
  // assets/es-vehicle-popover.js (2026-07-29) -- pill links for a facelift-specific generation
  // previously used the qualified tag ALONE, silently excluding every generation-wide product
  // that only carries the bare tag.
  const GEN_QUALIFIER_RE = /\s+(facelift|pre-facelift|lci)$/i;
  function bareParentTag(t){ return t.replace(GEN_QUALIFIER_RE, ''); }
  function hasQualifiedGenTag(tags){ return tags.some(t => t && bareParentTag(t) !== t); }
  function tagTermFor(t){
    const bare = bareParentTag(t);
    if (bare !== t) return `(${tagTerm(bare)} OR ${tagTerm(t)})`;
    return tagTerm(t);
  }
  // Pull any tag:"..."/tag:x sub-terms already present in the search query (present when this
  // page was reached by clicking a pill, which appends tag: terms) so the pill tree can tell
  // which make/model/gen is already active, same as collection mode reads current_tags.
  const searchTagMatches = Array.from(rawSearchTerms.matchAll(/tag:"([^"]+)"|tag:(\S+)/g)).map(m => (m[1] || m[2] || '').toLowerCase());
  // The free-text portion of the query, with any tag: sub-terms AND the "(...OR...)" grouping
  // punctuation those sub-terms may be wrapped in stripped out, so it can be preserved when
  // pills add/replace tag: terms. Stripping only the `tag:` tokens (the old regex) left a
  // dangling "( OR )" behind whenever the incoming query used the qualified-tag-OR-bare-parent
  // group above -- confirmed live: landing on a "(8V Facelift)" search result page and then
  // clicking any other pill carried a literal stray "( OR )" into every subsequent query.
  const baseSearchTerms = rawSearchTerms
    .replace(/\(\s*tag:(?:"[^"]+"|\S+)\s+OR\s+tag:(?:"[^"]+"|\S+)\s*\)/gi, '')
    .replace(/tag:"[^"]+"|tag:\S+/g, '')
    .replace(/\s+/g, ' ').trim();
  const currentTags = mode === 'search' ? searchTagMatches : currentTagsAttr;

  // Shopify's native collection tag-URL PATH routing (/collections/all/<tag>) silently 404s
  // for any tag containing a literal "." -- confirmed live: a product genuinely tagged
  // "mk7.5" (verified via its own product.tags) still 404s at /collections/all/mk7.5, while
  // the exact same tag works fine via /search?q=tag:"mk7.5". This looks like Shopify's router
  // misparsing the "." as a file-extension separator in the URL path (not present in a query
  // string). So any tag combination containing a "." routes through /search instead, even in
  // collection mode -- the only reliable way to reach those products at all.
  function hasDotTag(tags){ return tags.some(t => t && t.indexOf('.') !== -1); }

  function searchUrlFor(tags, baseTerms){
    const clean = tags.filter(Boolean);
    const q = [baseTerms || ''].concat(clean.map(tagTermFor)).filter(Boolean).join(' ');
    return `/search?q=${encodeURIComponent(q)}&type=product`;
  }

  function urlFor(tags){
    const clean = tags.filter(Boolean);
    // The path route (/collections/all/tag1+tag2) is a pure AND with no way to express the
    // qualified-tag-OR-bare-parent match tagTermFor builds -- needs the /search?q= route.
    if (mode === 'search' || hasDotTag(clean) || hasQualifiedGenTag(clean)) {
      return searchUrlFor(clean, mode === 'search' ? baseSearchTerms : '');
    }
    return clean.length ? collectionUrl + '/' + clean.map(t => encodeURIComponent(t)).join('+') : collectionUrl;
  }

  // A stable, canonical URL for the persistent vehicle-memory bar -- a plain /collections/all/
  // <tags> path when possible (works from any page), falling back to the /search?q=tag:...
  // form only when a "." tag or a qualified generation tag makes the collection path broken.
  function canonicalUrlFor(tags){
    const clean = tags.filter(Boolean);
    if (hasDotTag(clean) || hasQualifiedGenTag(clean)) return searchUrlFor(clean, '');
    return clean.length ? '/collections/all/' + clean.map(t => encodeURIComponent(t)).join('+') : '/collections/all';
  }

  function pillLink(label, tags, isActive, extraClass){
    const a = document.createElement('a');
    a.href = urlFor(tags);
    a.className = 'pill' + (extraClass ? ' ' + extraClass : '') + (isActive ? ' is-active' : '');
    a.textContent = label;
    return a;
  }
  function backPill(tags){
    const a = document.createElement('a');
    a.href = urlFor(tags);
    a.className = 'pill pill-ghost pill-back';
    a.setAttribute('aria-label', 'Back');
    a.innerHTML = '<span class="pill-back-label">← Back</span>' +
      '<svg class="pill-back-icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">' +
      '<path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
    return a;
  }

  // Existence-checking always parses the actual response and checks for a real product card
  // in #es-grid -- NOT just the HTTP status. Confirmed live that Shopify's collection tag-URL
  // route can return 200 with an EMPTY grid (not a 404) when every individual tag exists
  // somewhere in the store but never together on the same product (e.g. "infiniti" and
  // "es-brand-apr" each exist on real products, just never the same one) -- checking only
  // response.ok was a real bug that kept pills like "Infiniti" visible under an active "APR"
  // brand filter that has zero actual Infiniti-compatible products. 404 (a genuinely unknown
  // tag) and 200-with-empty-grid (a known combination with zero matches) both mean "hide this
  // pill" and are treated identically here.
  //  - No "." in any tag: the native /collections/all/<tags> route (still preferred when it
  //    works, since it's the real destination the pill links to).
  //  - Any "." tag (e.g. "mk7.5"): that route 404s even for genuinely tagged products (see
  //    hasDotTag's comment above), so this uses /search?q=tag:"x" tag:"y"... instead (every
  //    tag quoted, forcing exact-phrase matching -- unquoted decimal tags get tokenized and
  //    produce false positives, confirmed live).
  //  - Search mode: checked via the SAME urlFor() the pill's own link uses (a /search?q=
  //    query that includes the free-text search terms, not just the tag) -- checking only
  //    "/collections/all/<tag>" here (site-wide, ignoring the free-text scope) was a real bug:
  //    it kept "Infiniti" visible on an "exhaust" search results page even though EuroSpeed
  //    has zero real Infiniti exhaust products, because "infiniti" the tag exists somewhere
  //    in the store regardless of what's being searched for.
  const existsCache = {};
  async function tagsHaveResults(tags){
    const clean = tags.filter(Boolean);
    if (!clean.length) return true;
    const key = clean.slice().sort().join(',');
    if (existsCache[key] !== undefined) return existsCache[key];
    const promise = (async () => {
      try {
        const checkUrl = urlFor(clean);
        const r = await fetch(checkUrl, { cache: 'no-store' });
        if (!r.ok) return false;
        const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
        return doc.querySelector('#es-grid .product-item') !== null;
      } catch (_) { return true; } // don't hide pills if the check itself fails
    })();
    existsCache[key] = promise;
    const result = await promise;
    existsCache[key] = result;
    return result;
  }

  // Fetches every product for a DIFFERENT tag combination (the qualified generation, e.g.
  // "...+mk7.5") than the one already server-rendered into #es-grid (the bare-code page, e.g.
  // "...+mk7"), across all its pages, and appends any not already shown (deduped by product
  // handle) -- so a facelift/sub-generation page shows both the universal bare-code products
  // AND the facelift-specific ones, instead of only whichever tag the URL itself used.
  async function applyGenerationUnion(tags){
    const grid = document.getElementById('es-grid');
    if (!grid) return;
    const have = new Set(Array.from(grid.querySelectorAll('.product-item a[href*="/products/"]')).map(a => {
      const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      return m ? m[1] : null;
    }).filter(Boolean));

    const path = urlFor(tags);
    const parser = new DOMParser();
    for (let page = 1; page <= 20; page++){
      const sep = path.indexOf('?') !== -1 ? '&' : '?';
      let res;
      try { res = await fetch(`${path}${sep}page=${page}`, { cache: 'no-store' }); }
      catch (_) { break; }
      if (!res.ok) break;
      const doc = parser.parseFromString(await res.text(), 'text/html');
      const items = Array.from(doc.querySelectorAll('#es-grid .product-item'));
      if (!items.length) break;
      items.forEach(item => {
        const link = item.querySelector('a[href*="/products/"]');
        const m = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        const handle = m ? m[1] : null;
        if (handle && !have.has(handle)) {
          have.add(handle);
          grid.insertAdjacentHTML('beforeend', item.outerHTML);
        }
      });
      if (items.length < 100) break;
    }
  }

  async function render(FIT){
    root.innerHTML = '';
    const makeKeys = Object.keys(FIT).filter(m => FIT[m] && Object.keys(FIT[m]).length);
    if (!makeKeys.length) return;

    let activeMake = null;
    const makeIsForced = mode === 'collection' && !!forcedMakeTag;
    for (let i = 0; i < makeKeys.length; i++){
      const aliases = aliasesFor(MAKE_TAGS, makeKeys[i]);
      if (makeIsForced ? aliases.indexOf(forcedMakeTag) !== -1 : aliases.some(a => currentTags.indexOf(a) !== -1)) {
        activeMake = makeKeys[i]; break;
      }
    }

    let activeModel = null;
    if (activeMake){
      const modelKeys = Object.keys(FIT[activeMake] || {});
      for (let i = 0; i < modelKeys.length; i++){
        const aliases = aliasesFor(MODEL_TAGS, modelKeys[i]);
        if (aliases.some(a => currentTags.indexOf(a) !== -1)) { activeModel = modelKeys[i]; break; }
      }
    }

    let activeGenIdx = -1, genEntries = [];
    if (activeMake && activeModel){
      genEntries = FIT[activeMake][activeModel] || [];
      for (let i = 0; i < genEntries.length; i++){
        const tags = (genEntries[i].searchTags || []).map(t => t.toLowerCase());
        if (tags.some(t => currentTags.indexOf(t) !== -1)) { activeGenIdx = i; break; }
      }
    }

    const realMakeTag = activeMake ? primaryTag(MAKE_TAGS, activeMake) : null;
    const makeTagForLinks = makeIsForced ? null : realMakeTag;

    // Record the current vehicle scope for the persistent header bar (assets/
    // vehicle-memory-bar.js) whenever this page itself represents a specific make+model (with
    // or without a generation) -- covers arriving here via a pill click just as much as via the
    // homepage selector's own save call in ymm-filter.js.
    if (window.ESVehicleMemory && activeMake && activeModel){
      const modelTagForMemory = primaryTag(MODEL_TAGS, activeModel);
      const genEntry = activeGenIdx >= 0 ? genEntries[activeGenIdx] : null;
      const label = activeMake + ' ' + activeModel + (genEntry && genEntry.label ? ' ' + genEntry.label : '');
      const memTags = [realMakeTag, modelTagForMemory];
      if (genEntry && genEntry.searchTags && genEntry.searchTags[0]) memTags.push(genEntry.searchTags[0].toLowerCase());
      window.ESVehicleMemory.save(label, canonicalUrlFor(memTags), memTags);
    }

    const bar = document.createElement('div');
    bar.className = 'pill-bar';
    bar.style.setProperty('--brand', brandHex);
    const inner = document.createElement('div');
    inner.className = 'pill-bar-inner';

    const filterTags = activeFilterTags();

    if (!activeMake){
      if (makeKeys.length < 2) return;
      const candidates = makeKeys.map(mk => ({ mk, tag: primaryTag(MAKE_TAGS, mk) }));
      const checks = await Promise.all(candidates.map(c => tagsHaveResults([c.tag, ...filterTags])));
      const valid = candidates.filter((c, i) => checks[i]);
      if (!valid.length) return;
      inner.appendChild(pillLink('All', [], currentTags.length === 0));
      valid.forEach(c => inner.appendChild(pillLink(c.mk, [c.tag], false)));
    } else if (!activeModel){
      const modelKeys = Object.keys(FIT[activeMake] || {});
      if (!modelKeys.length) return;
      const candidates = modelKeys.map(md => ({ md, tag: primaryTag(MODEL_TAGS, md) }));
      const checks = await Promise.all(candidates.map(c => tagsHaveResults([realMakeTag, c.tag, ...filterTags])));
      const valid = candidates.filter((c, i) => checks[i]);
      if (!valid.length) return;
      inner.appendChild(backPill([]));
      inner.appendChild(pillLink('All ' + activeMake + ' Models', makeTagForLinks ? [makeTagForLinks] : [], true));
      valid.forEach(c => inner.appendChild(pillLink(c.md, makeTagForLinks ? [makeTagForLinks, c.tag] : [c.tag], false)));
    } else {
      if (!genEntries.length) return;
      const modelTag = primaryTag(MODEL_TAGS, activeModel);
      // Each entry's searchTags is a real array of every distinct chassis tag actually used in
      // that (possibly merged) generation bucket -- worker1 now merges sub-generations like
      // "mk7" + "mk7.5" into one bucket server-side (matching GERSpeed's own selector, which
      // shows a single "MK7 · MK7.5" option rather than two a customer has to guess between).
      // Every tag in the bucket is checked independently: the first one with real results
      // becomes the pill's own URL (a collection tag-URL can only use one at a time), and any
      // OTHER tags in the bucket that also have real results get merged in via
      // applyGenerationUnion once this page loads, so a real MK7.5-specific product isn't
      // missed just because the URL itself uses the bare "mk7" tag.
      const checks = await Promise.all(genEntries.map(async entry => {
        const tags = (entry && entry.searchTags && entry.searchTags.length) ? entry.searchTags.map(t => t.toLowerCase()) : [];
        if (!tags.length) return { ok: false, tag: '', unionTags: [] };
        const results = await Promise.all(tags.map(t => tagsHaveResults([realMakeTag, modelTag, t, ...filterTags])));
        const validTags = tags.filter((t, i) => results[i]);
        if (!validTags.length) return { ok: false, tag: tags[0], unionTags: [] };
        return { ok: true, tag: validTags[0], unionTags: validTags.slice(1) };
      }));
      const valid = genEntries.map((entry, i) => ({ entry, i, ...checks[i] })).filter(x => x.ok);
      if (!valid.length) return;
      inner.appendChild(backPill(makeTagForLinks ? [makeTagForLinks] : []));
      // 2026-11-03 (Jeff): remember the destination vehicle the instant a generation pill is
      // clicked, BEFORE the browser navigates. Saving only after this page's async fitment
      // fetch left the remembered vehicle (and the sticky bar) on the previous generation until
      // a second click.
      const rememberOnClick = function(a, label, memTags){
        a.addEventListener('click', function(){
          if (window.ESVehicleMemory) window.ESVehicleMemory.save(label, canonicalUrlFor(memTags), memTags);
        });
        return a;
      };
      const modelTagMem = primaryTag(MODEL_TAGS, activeModel);
      inner.appendChild(rememberOnClick(pillLink('All Generations', makeTagForLinks ? [makeTagForLinks, modelTag] : [modelTag], activeGenIdx === -1), activeMake + ' ' + activeModel, [realMakeTag, modelTagMem]));
      valid.forEach(x => {
        const entry = x.entry;
        const label = entry && entry.label ? entry.label : String(entry);
        const tags = makeTagForLinks ? [makeTagForLinks, modelTag, x.tag] : [modelTag, x.tag];
        const gt = (entry && entry.searchTags && entry.searchTags[0]) ? entry.searchTags[0].toLowerCase() : x.tag;
        inner.appendChild(rememberOnClick(pillLink(label, tags, x.i === activeGenIdx), activeMake + ' ' + activeModel + ' ' + label, [realMakeTag, modelTagMem, gt]));
      });

      // If the page we're CURRENTLY ON is itself one of these merged-bucket entries with more
      // than one real tag (i.e. the user just landed here, possibly via the pill just built
      // above), fetch and merge in each additional tag's own results so they see everything in
      // the bucket, not just whichever single tag the URL itself happened to use.
      const activeValid = valid.find(x => x.i === activeGenIdx);
      if (activeValid && activeValid.unionTags.length) {
        activeValid.unionTags.forEach(unionTag => {
          const unionTags = makeTagForLinks ? [makeTagForLinks, modelTag, unionTag] : [modelTag, unionTag];
          applyGenerationUnion(unionTags);
        });
      }
    }

    bar.appendChild(inner);
    root.appendChild(bar);
  }

  if (!FITMENT_URL && !window.__esFitment) return;
  let lastFIT = {};
  // Prefer the early <head> prefetch (window.__esFitment) so pills render without a late fetch.
  (window.__esFitment ? window.__esFitment.catch(function(){ return null; }) : Promise.resolve(null))
    .then(function(pre){ return pre || (FITMENT_URL ? fetch(FITMENT_URL, { cache:'no-store' }).then(function(res){ return res.ok ? res.json() : {}; }) : {}); })
    .then(function(data){ lastFIT = esR8Labels((data && typeof data === 'object') ? data : {}); render(lastFIT); })
    .catch(function(){ /* leave pills absent on failure -- no broken UI */ });

  // Re-validate pills whenever a Category/Brand checkbox changes (assets/category-brand-
  // filters.js) -- e.g. selecting the "APR" brand should immediately drop Make pills for makes
  // APR doesn't sell anything for, instead of leaving stale pills up that lead to zero results
  // once combined with the active brand filter.
  document.addEventListener('change', e => {
    if (e.target && (e.target.name === 'es-category' || e.target.name === 'es-brand')) render(lastFIT);
  });
})();
